import type { ReactNode } from 'react';

import { Card, CardContent } from '@pesto/ui/components/Card';
import { Typography } from '@pesto/ui/components/Typography';
import { format } from 'date-fns';

import type { CardViewModel } from '../types/Cards.types';

import { CardVisual } from './CardVisual';

interface CardSummaryProps {
  card: CardViewModel;
  actions?: ReactNode;
}

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? value : format(parsed, 'MMM d, yyyy');
};

const Field = ({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) => (
  <div className="space-y-1">
    <Typography variant="caption" className="text-muted-foreground uppercase tracking-wide">
      {label}
    </Typography>
    <div className={`text-foreground text-sm ${mono ? 'font-mono' : ''}`}>{value ?? '—'}</div>
  </div>
);

export const CardSummary = ({ card, actions }: CardSummaryProps) => {
  return (
    <Card>
      <CardContent className="flex flex-col gap-6 p-6 md:flex-row md:items-stretch">
        <div className="flex shrink-0 items-center justify-center md:w-72">
          <CardVisual card={card} />
        </div>
        <div className="flex flex-1 flex-col gap-6">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Typography variant="headerLarge" className="text-xl font-semibold">
                {card.referenceId ? `Card ${card.referenceId}` : 'Card'}
              </Typography>
              <Typography variant="caption" className="text-muted-foreground font-mono">
                ID {card.id}
              </Typography>
            </div>
            {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-3">
            <Field label="Opened" value={formatDate(card.dateOpened)} />
            <Field label="Created" value={formatDate(card.insertedAt)} />
            {card.processor && <Field label="Processor" value={card.processor} />}
            {card.processor === 'LITHIC' && <Field label="Lithic card token" value={card.referenceId} mono />}
            {card.blockedReason && <Field label="Blocked reason" value={card.blockedReason} />}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
